import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation
} from '@angular/core';
import { GlobalWorkerOptions, getDocument, OutputScale, TextLayer } from 'pdfjs-dist/legacy/build/pdf.mjs';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';

const PDFJS_ASSETS = 'assets/pdfjs/';
const CSS_UNITS = 96 / 72;

export type PdfSource = string | Uint8Array;

export interface PdfProgress {
  loaded: number;
  total: number;
}

@Component({
  selector: 'app-pdf-page',
  template: `<div class="pdf-page-container" #host><div class="pdfViewer singlePageView"><div class="page" #page>
    <div class="canvasWrapper"><canvas #canvas></canvas></div><div class="textLayer" #textLayer></div>
  </div></div></div>`,
  styleUrls: ['./pdf-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PdfPageComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() src: PdfSource;
  @Input() renderText = true;
  @Input() originalSize = true;

  @Input()
  set page(value: number) {
    if (value === this._page) {
      return;
    }
    this._page = value;
    this.scheduleRender();
  }
  get page() {
    return this._page;
  }

  @Input()
  set zoom(value: number) {
    if (!(value > 0) || value === this._zoom) {
      return;
    }
    this._zoom = value;
    this.scheduleRender();
  }
  get zoom() {
    return this._zoom;
  }

  private _page = 1;
  private _zoom = 1;
  private renderScheduled = false;
  private firstRenderDone = false;

  @Output() loaded = new EventEmitter<PDFDocumentProxy>();
  @Output() progress = new EventEmitter<PdfProgress>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() error = new EventEmitter<unknown>();

  @ViewChild('host', { static: true }) private host: ElementRef<HTMLDivElement>;
  @ViewChild('page', { static: true }) private pageElement: ElementRef<HTMLDivElement>;
  @ViewChild('canvas', { static: true }) private canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('textLayer', { static: true }) private textLayerElement: ElementRef<HTMLDivElement>;

  private loadingTask: PDFDocumentLoadingTask;
  private document: PDFDocumentProxy;
  private renderTask: RenderTask;
  private textLayer: TextLayer;
  private renderId = 0;
  private resizeObserver: ResizeObserver;
  private renderedWidth = 0;

  constructor(private zone: NgZone) {
    GlobalWorkerOptions.workerSrc = new URL(`${PDFJS_ASSETS}pdf.worker.min.mjs`, document.baseURI).href;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['src']) {
      this.load();
    } else if (changes['renderText'] || changes['originalSize']) {
      this.scheduleRender();
    }
  }

  private scheduleRender() {
    if (!this.document || this.renderScheduled) {
      return;
    }
    this.renderScheduled = true;
    queueMicrotask(() => {
      this.renderScheduled = false;
      this.render();
    });
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.host.nativeElement.clientWidth;
        if (!this.originalSize && width && Math.abs(width - this.renderedWidth) >= 1) {
          requestAnimationFrame(() => this.scheduleRender());
        }
      });
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.cancelRender();
    this.destroyDocument();
  }

  private load() {
    this.cancelRender();
    this.destroyDocument();
    if (!this.src) {
      return;
    }
    const assets = new URL(PDFJS_ASSETS, document.baseURI).href;
    const source = typeof this.src === 'string' ? { url: this.src } : { data: this.src };
    this.loadingTask = this.zone.runOutsideAngular(() => getDocument({
      ...source,
      cMapUrl: `${assets}cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `${assets}standard_fonts/`,
      wasmUrl: `${assets}wasm/`,
      iccUrl: `${assets}iccs/`,
      enableXfa: false
    }));
    const task = this.loadingTask;
    task.onProgress = (data: PdfProgress) => this.zone.run(() => this.progress.emit(data));
    task.promise.then(
      pdf => {
        if (task !== this.loadingTask) {
          return;
        }
        this.document = pdf;
        this.firstRenderDone = false;
        this.render();
      },
      err => {
        if (task === this.loadingTask) {
          this.zone.run(() => this.error.emit(err));
        }
      }
    );
  }

  private async render() {
    const pdf = this.document;
    if (!pdf) {
      return;
    }
    const pageNumber = Math.min(Math.max(1, Math.round(Number(this.page) || 1)), pdf.numPages);
    if (pageNumber !== this.page) {
      queueMicrotask(() => this.zone.run(() => this.pageChange.emit(pageNumber)));
    }
    const id = ++this.renderId;
    this.cancelRender();
    try {
      const page = await pdf.getPage(pageNumber);
      if (id !== this.renderId) {
        return;
      }
      await this.zone.runOutsideAngular(() => this.drawPage(page, id));
      if (id === this.renderId && !this.firstRenderDone) {
        this.firstRenderDone = true;
        this.zone.run(() => this.loaded.emit(pdf));
      }
    } catch (err) {
      if ((err as { name?: string })?.name !== 'RenderingCancelledException' && id === this.renderId) {
        this.zone.run(() => this.error.emit(err));
      }
    }
  }

  private async drawPage(page: PDFPageProxy, id: number) {
    const unscaled = page.getViewport({ scale: 1 });
    const scale = (this.originalSize ? this.zoom * CSS_UNITS : this.fitWidthScale(unscaled.width)) || 1;
    const viewport = page.getViewport({ scale });

    const pageDiv = this.pageElement.nativeElement;
    pageDiv.style.width = `${Math.floor(viewport.width)}px`;
    pageDiv.style.height = `${Math.floor(viewport.height)}px`;
    pageDiv.style.setProperty('--scale-factor', `${viewport.scale}`);
    pageDiv.style.setProperty('--user-unit', `${viewport.userUnit}`);

    const canvas = this.canvas.nativeElement;
    const outputScale = new OutputScale();
    canvas.width = Math.floor(viewport.width * outputScale.sx);
    canvas.height = Math.floor(viewport.height * outputScale.sy);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;

    this.renderTask = page.render({
      canvas,
      viewport,
      transform: outputScale.scaled ? [outputScale.sx, 0, 0, outputScale.sy, 0, 0] : undefined
    });
    await this.renderTask.promise;
    if (id !== this.renderId) {
      return;
    }

    const textDiv = this.textLayerElement.nativeElement;
    textDiv.replaceChildren();
    if (this.renderText) {
      this.textLayer = new TextLayer({ textContentSource: page.streamTextContent(), container: textDiv, viewport });
      await this.textLayer.render();
    }
  }

  private fitWidthScale(pageWidth: number) {
    const width = this.host.nativeElement.clientWidth;
    this.renderedWidth = width;
    return width && pageWidth ? (width / pageWidth) * this.zoom : 0;
  }

  private cancelRender() {
    this.renderTask?.cancel();
    this.renderTask = null;
    this.textLayer?.cancel();
    this.textLayer = null;
  }

  private destroyDocument() {
    const task = this.loadingTask;
    this.loadingTask = null;
    this.document = null;
    task?.destroy();
  }
}
